'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@//components/Button';
import { Disclaimer } from '@//components/Disclaimer';
import { Link } from '@//components/Link';
import { Textfield } from '@//containers/Textfield';

const SignInModal = lazy(() => import('@//containers/SignInModal'));

import { FetchError } from '@//data/httpClient';
import useGetVehicleByRegistrationNumberMinimalRateLimited from '@//data/vehicles/useGetVehicleByRegistrationNumberMinimalRateLimited';
import { useAuth } from '@//providers/AuthProvider';
import { useMarket, useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { odometerReadingUnitByMarket } from '@//utils/odometer';
import {
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from '@//utils/safe-storage';
import { type ValuationSchemaType, valuationSchema } from './utils';
import styles from './ValuationForm.module.scss';

const setFormLocalStorage = (data: ValuationSchemaType) =>
  safeLocalStorageSetItem('valuationForm', JSON.stringify(data));

const getFormLocalStorage = (): ValuationSchemaType | undefined => {
  if (typeof window !== 'object') return undefined;
  const data = safeLocalStorageGetItem('valuationForm');
  if (data) {
    return JSON.parse(data) as ValuationSchemaType;
  }
  return undefined;
};

const clearFormLocalStorage = () => safeLocalStorageRemoveItem('valuationForm');

const inputMode = 'numeric';
const pattern = '[0-9]*';

interface ValuationFormProps {
  valuationPagePath: string;
}

const ValuationForm = ({ valuationPagePath }: ValuationFormProps) => {
  const searchParams = useSearchParams();
  const autoProceed = useMemo(() => searchParams.has('autoProceed'), []);

  const t = useTranslations('components.valuateVehicleForm');
  const tVehicleGlossary = useTranslations('utils.vehicleGlossary');
  const tValidation = useTranslations('utils.validation');
  const tSignInModal = useTranslations('components.signInModal.valuation');
  const market = useMarket();
  const getVehicleMutation = useGetVehicleByRegistrationNumberMinimalRateLimited();
  const router = useRouter();
  const pathname = usePathname();
  const { decodedToken } = useAuth();
  const [signInModal, setSignInModal] = useState(false);
  const marketMetadata = useMarketMetadata();

  const errorMessage = useMemo(() => {
    if (!getVehicleMutation.error) return undefined;

    if (getVehicleMutation.error instanceof FetchError) {
      switch (getVehicleMutation.error.status) {
        case 429:
          return t('errors.tooManyRequests');
        case 404:
          return t('errors.vehicleNotFound');
        case 500:
          return t('errors.unableToFetch');
        default:
          return t('errors.vehicleNotFound');
      }
    }

    return t('errors.vehicleNotFound');
  }, [getVehicleMutation.error, t]);

  const defaultValues = useMemo(() => getFormLocalStorage(), []);

  const {
    formState: { errors },
    handleSubmit,
    register,
    getValues,
    setError,
    clearErrors,
  } = useForm({
    defaultValues,
    resolver: zodResolver(valuationSchema(market, tValidation)),
  });

  const onSubmit = handleSubmit((data) => {
    // Clear any previous API errors
    clearErrors('registrationNumber');

    if (decodedToken) {
      // Reset any previous errors before submitting
      if (getVehicleMutation.isError) {
        getVehicleMutation.reset();
      }
      getVehicleMutation.mutate({
        registrationNumber: data.registrationNumber.replace(/\s/g, ''),
        tryResolveFullDataSilent: true,
      });
    } else {
      setFormLocalStorage(data);
      setSignInModal(true);
    }
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  useEffect(() => {
    if (autoProceed) {
      router.replace(pathname);
      onSubmit();
    }
  }, []);

  useEffect(() => {
    if (getVehicleMutation.isSuccess) {
      clearFormLocalStorage();

      const { registrationNumber, odometerReading } = getValues();

      router.push(
        `${valuationPagePath}/${registrationNumber.replace(/\s/g, '')}/${odometerReading}`,
      );
    }
  }, [getVehicleMutation.isSuccess]);

  useEffect(() => {
    if (getVehicleMutation.isError && errorMessage) {
      setError('registrationNumber', {
        type: 'manual',
        message: errorMessage,
      });
    }
  }, [getVehicleMutation.isError, errorMessage, setError]);

  const isPending = getVehicleMutation.isPending || getVehicleMutation.isSuccess;

  return (
    <>
      <Suspense fallback={null}>
        <SignInModal
          title={tSignInModal('title')}
          isOpen={signInModal}
          description={tSignInModal('description')}
          interactionType="Start application"
          redirectUrl={`${pathname}?autoProceed=true`}
          onClose={() => setSignInModal(false)}
        />
      </Suspense>
      <Textfield
        id="input-regnr"
        label={tVehicleGlossary('registrationNumber')}
        licensePlateBadge={true}
        state={errors.registrationNumber?.message ? 'error' : undefined}
        stateMessage={errors.registrationNumber?.message}
        disabled={isPending}
        onKeyDown={handleKeyDown}
        {...register('registrationNumber')}
      />
      <Textfield
        id="input-mileage"
        type="number"
        label={tVehicleGlossary('odometerReading.label')}
        unit={tVehicleGlossary(`odometerReading.units.${odometerReadingUnitByMarket[market]}`)}
        state={errors.odometerReading?.message ? 'error' : undefined}
        stateMessage={errors.odometerReading?.message}
        inputMode={inputMode}
        pattern={pattern}
        disabled={isPending}
        onKeyDown={handleKeyDown}
        {...register('odometerReading', {
          valueAsNumber: true,
        })}
      />
      <Button
        type="button"
        title={t('actions.create.title')}
        aria-label={t('actions.create.title')}
        size="lg"
        variant="primary"
        fullWidth
        onClick={onSubmit}
        disabled={isPending}
        loading={isPending}
      >
        {t('actions.create.label')}
      </Button>
      <Disclaimer className={styles.disclaimer}>
        {t.rich('disclaimer.privacyText', {
          link: (chunks) => (
            <Link
              href={marketMetadata.valuateVehicle.privacyPolicyLink}
              title={t('disclaimer.privacyLinkTitle')}
            >
              {chunks}
            </Link>
          ),
        })}
      </Disclaimer>
    </>
  );
};

export default ValuationForm;
